import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";
import { COMPANY_FEATURES } from "@routebot/constants";

import { getColumnValue } from "shared/util/datagrid";

import Layout from "layouts/LayoutMasterDetail";

import StudentItem from "./StudentItem";
import { useBundledRouteApi } from "features/bundled-routes/hooks/useBundledRouteApi";
import { noRealSchoolBlocker } from "shared/utility/blocker-conditions";
import { GetParentNamesWithPhoneNumberFromStudent } from "shared/utility/relational-data";
import { isInternationalTheme } from "config/ConfigUtils";

const StudentsList = (props) => {
  const { t } = useTranslation();
  const listName = "main_students";
  const itemType = "student";

  const {
    students,
    schools,
    vehicleRoutes,
    DeleteStudent,
    InsertStudent,
    UpdateStudent,
    schoolOptions,
    userMap,
    studentToParentMap,
  } = useContext(StoreContext);

  const auth = useContext(AuthContext);
  const { listBundledRoutes } = useBundledRouteApi();
  const [bundledRoutes, setBundledRoutes] = useState([]);
  const bundledEnabled = !!auth?.company?.features?.[COMPANY_FEATURES.BUNDLED_STUDENT_ROUTES];

  // Bundled routes live outside the global store (feature-local). Fetch them here ONLY when the
  // company has the feature — otherwise the list behaves exactly as before (endpoint is gated).
  useEffect(() => {
    if (!bundledEnabled) return;
    let alive = true;
    (async () => {
      const data = await listBundledRoutes();
      if (alive) setBundledRoutes(Array.isArray(data) ? data : []);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundledEnabled]);

  // All services (vehicle routes + bundled routes) that carry this student in the given direction.
  const collectServiceNames = (studentId, vehicleType, bundledType) => {
    const id = studentId?.toString();
    const names = [];
    try {
      vehicleRoutes.forEach((route) => {
        if (route.type?.toString() === vehicleType && route.student?.some((s) => s.id?.toString() === id)) {
          names.push(route.name);
        }
      });
      bundledRoutes.forEach((route) => {
        if (route.routeType === bundledType && route.student?.some((s) => (s.id || s._id)?.toString() === id)) {
          names.push(route.name);
        }
      });
    } catch {
      /* defensive: never break the grid over a malformed row */
    }
    return names;
  };

  const cellLineStyle = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    marginBottom: "5px",
    marginTop: "5px",
  };

  const renderServiceCell = (names) => {
    if (!names?.length) return <div style={cellLineStyle}>-</div>;
    return (
      <div style={{ width: "100%" }}>
        {names.map((name, index) => (
          <div key={index} style={cellLineStyle} title={name}>
            {name}
          </div>
        ))}
      </div>
    );
  };

  let pageItems;

  if (props?.students) {
    pageItems = props.students;
  } else {
    pageItems = students;
  }

  let listColumns = [];

  if (schoolOptions?.length > 1) {
    listColumns.push(getColumnValue("school", t("client"), schoolOptions, "school", 300));
  }
  listColumns.push({ field: "name", headerName: t("name"), width: 350 });

  listColumns.push({
    field: "routemorning",
    headerName: t("pickUpService"),
    width: 250,
    valueGetter: (params) => {
      const names = collectServiceNames(params.row.id, "100", "PICK_UP");
      return names.length ? names.join(", ") : "-";
    },
    renderCell: (params) => renderServiceCell(collectServiceNames(params.row.id, "100", "PICK_UP")),
  });

  listColumns.push({
    field: "routeevening",
    headerName: t("dropOffService"),
    width: 250,
    valueGetter: (params) => {
      const names = collectServiceNames(params.row.id, "200", "DROP_OFF");
      return names.length ? names.join(", ") : "-";
    },
    renderCell: (params) => renderServiceCell(collectServiceNames(params.row.id, "200", "DROP_OFF")),
  });

  listColumns.push({
    field: "parents",
    headerName: t("parents"),
    width: 350,
    height: 100,
    valueGetter: (params) => {
      const parentNames = GetParentNamesWithPhoneNumberFromStudent(userMap, studentToParentMap, params.row.id);
      return parentNames?.length ? parentNames.join(", ") : "-";
    },
    renderCell: (params) => {
      const parentNames = GetParentNamesWithPhoneNumberFromStudent(userMap, studentToParentMap, params.row.id);
      if (!parentNames?.length)
        return (
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%", // veya sabit: "330px"
              marginBottom: "5px",
              marginTop: "5px",
            }}
          >
            -
          </div>
        );

      return (
        <div style={{ width: "100%" }}>
          {parentNames.map((name, index) => (
            <div
              key={index}
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
                marginBottom: "5px",
                marginTop: "5px",
              }}
              title={name} // hover'da tam göster
            >
              {name}
            </div>
          ))}
        </div>
      );
    },
  });

  listColumns.push({ field: "identifier", headerName: t("description"), width: 450 });

  listColumns.push({
    field: "isActive",
    headerName: t("status"),
    width: 100,
    valueGetter: (params) => {
      const student = students.find((student) => student.id.toString() === params.row.id.toString());
      if (!student.hasOwnProperty("isActive")) {
        return "Aktif";
      } else {
        return student.isActive ? t("active") : t("inactive");
      }
    },
  });

  listColumns.push({ field: "level", headerName: t("studentGrade"), width: 100 });

  // listColumns.push({ field: "phonenumber", headerName: t("phone"), width: 350, hide: true });
  // listColumns.push({ field: "addressgeton", headerName: t("pickUpAddress"), width: 150, hide: true });
  // listColumns.push({ field: "addressgetoff", headerName: t("dropOffAddress"), width: 100, hide: true });

  return (
    <Layout
      itemType={itemType}
      items={pageItems}
      listName={listName}
      listColumns={listColumns}
      SelectedItemComponent={StudentItem}
      NewItemComponent={StudentItem}
      DeleteItem={DeleteStudent}
      InsertItem={InsertStudent}
      UpdateItem={UpdateStudent}
      hideCreateNewItemWhenSelected={true}
      showImportSpreadsheet={isInternationalTheme()}
      blockerConditions={[noRealSchoolBlocker(schools)]}
    />
  );
};

export default StudentsList;
